import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { apiConstant, roleConstant } from 'src/app/app.constant';

type SupportTopic = {
  value: string;
  label: string;
};

@Component({
  selector: 'app-contact-support-modal',
  templateUrl: './contact-support-modal.component.html',
  styleUrls: ['./contact-support-modal.component.css'],
})
export class ContactSupportModalComponent implements OnInit {
  topics: SupportTopic[] = [
    { value: 'general', label: 'General question' },
    { value: 'timesheets', label: 'Timesheets' },
    { value: 'invoices', label: 'Invoices' },
    { value: 'reports', label: 'Reports & exports' },
    { value: 'integrations', label: 'Integrations (Sage, Xero, QuickBooks…)' },
    { value: 'billing', label: 'Billing & subscription' },
    { value: 'bug', label: "Something's broken" },
    { value: 'other', label: 'Other' },
  ];

  topic = 'general';
  subject = '';
  message = '';
  includeDiagnostic = true;

  attachment: File | null = null;
  attachmentLabel = 'Attach file or screenshot';

  sending = false;
  errorMessage = '';
  sent = false;
  ticketId = '';

  userEmail = '';
  userRoleLabel = '';
  userName = '';
  pageLabel = '';
  userAgentLabel = '';

  subjectInvalid = false;
  messageInvalid = false;

  constructor(
    private activeModal: NgbActiveModal,
    private http: HttpClient,
    private authService: AuthenticationService,
  ) {}

  ngOnInit(): void {
    const user: any = this.authService.userValue || {};
    this.userEmail = user.email || '';
    this.userName = user.name || [user.first_name, user.last_name].filter(Boolean).join(' ');

    const roleLabels: Record<string, string> = {
      [roleConstant.AGENCY]: 'Agency Admin',
      [roleConstant.CLIENT]: 'Client',
      [roleConstant.CANDIDATE]: 'Contractor',
      [roleConstant.SUPER_ADMIN]: 'Super Admin',
    };
    this.userRoleLabel = roleLabels[user.role_uuid] || 'User';

    this.pageLabel = this.derivePageLabel(window.location.pathname);
    this.userAgentLabel = navigator.userAgent;
  }

  private derivePageLabel(path: string): string {
    const segment = (path || '/').split('/').filter(Boolean)[0] || 'Dashboard';
    return segment
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  toggleDiagnostic(): void {
    this.includeDiagnostic = !this.includeDiagnostic;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0] || null;
    this.attachment = file;
    if (file) {
      const sizeKb = Math.max(1, Math.round(file.size / 1024));
      this.attachmentLabel = `${file.name} · ${sizeKb} KB`;
    } else {
      this.attachmentLabel = 'Attach file or screenshot';
    }
  }

  send(): void {
    this.errorMessage = '';
    this.subjectInvalid = !this.subject.trim();
    this.messageInvalid = !this.message.trim();
    if (this.subjectInvalid || this.messageInvalid) {
      return;
    }

    this.sending = true;

    const formData = new FormData();
    formData.append('topic', this.topic);
    formData.append('subject', this.subject.trim());
    formData.append('message', this.message.trim());
    formData.append('include_diagnostic', this.includeDiagnostic ? '1' : '0');
    if (this.includeDiagnostic) {
      formData.append('page', this.pageLabel);
      formData.append('user_agent', this.userAgentLabel);
    }
    if (this.attachment) {
      formData.append('attachment', this.attachment);
    }

    this.http
      .post<{ message: string; ticket_id: string }>(
        `${environment.baseUrl}${apiConstant.SUPPORT_CONTACT}`,
        formData,
      )
      .subscribe({
        next: (res) => {
          this.ticketId = res.ticket_id;
          this.sent = true;
          this.sending = false;
        },
        error: (err: HttpErrorResponse) => {
          this.sending = false;
          this.errorMessage = err?.error?.message || 'Could not send your message. Please try again.';
        },
      });
  }

  close(): void {
    this.activeModal.close();
  }
}
